/**
 * Zod schemas for every entity the API and the PWA exchange, plus the
 * create/update input shapes for `Lead` and `Product`. Types are derived via
 * `z.infer` — never hand-write a type that duplicates a schema; they drift.
 *
 * See docs/plan.md, Data Model, for the Prisma models these mirror.
 */
import { z } from 'zod';
import { CurrencyCode, MoneyAmount } from './money.js';
import { DimensionUnit, LeadStage, PaymentMode } from './enums.js';
/**
 * A database row id. Deliberately format-agnostic (not `z.uuid()` or a cuid
 * pattern): the concrete id strategy for our own tables is Prisma's to pick
 * and is not yet fixed, whereas Better Auth's `user.id` is a cuid by
 * contract. Tightening this once the Prisma schema lands is a non-breaking
 * narrowing, not a redesign.
 */
export const Id = z.string().min(1);
export const Event = z.object({
    id: Id,
    name: z.string().min(1),
    city: z.string().min(1),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    isActive: z.boolean()
});
export const Category = z.object({
    id: Id,
    name: z.string().min(1),
    sortOrder: z.number().int(),
    isActive: z.boolean()
});
export const Material = z.object({
    id: Id,
    name: z.string().min(1),
    sortOrder: z.number().int(),
    isActive: z.boolean()
});
export const Finish = z.object({
    id: Id,
    name: z.string().min(1),
    sortOrder: z.number().int(),
    isActive: z.boolean()
});
export const ProductPhoto = z.object({
    id: Id,
    productId: Id,
    // Content-hashed storage keys, served through the auth-gated media route. Two
    // DERIVED files per photo from the one resize pipeline: `coverPath` ~800px,
    // `thumbPath` ~240px. Both are precached; the manifest emits one asset per file.
    coverPath: z.string().min(1),
    thumbPath: z.string().min(1),
    // The retained full-resolution ORIGINAL's key (its own true-format extension),
    // for the office's download action. Null for photos added before the feature
    // (their original was discarded). NOT precached — an online-only download.
    originalPath: z.string().min(1).nullable(),
    sortOrder: z.number().int().nonnegative(),
    createdAt: z.iso.datetime()
});
export const Product = z.object({
    id: Id,
    itemNo: z.number().int().positive(),
    name: z.string().min(1),
    // Optional on purpose: blank is the supported way to say "not sure yet" —
    // there is deliberately no "Other" option. See docs/plan.md, Entity Behaviour.
    categoryId: Id.nullable().optional(),
    // A product can be made of several materials (many-to-many) — an id list,
    // empty when none are tagged.
    materialIds: z.array(Id),
    // A product carries several finishes (many-to-many) — one catalogue row today
    // covers every finish it is made in, because the split into one SKU per
    // finish is deferred. An id list, empty when none are tagged.
    finishIds: z.array(Id),
    // Blank on all but 2 of ~152 source rows. Format with `formatMoney`, which
    // renders a missing amount as '—', never '₹0'.
    listPrice: MoneyAmount.nullable().optional(),
    currency: CurrencyCode,
    // Physical size in HUNDREDTHS of the product's unit (`dimensionUnit`), exact
    // integers, stored exactly as measured (no cm↔inch conversion — it is lossy).
    // Box dimensions (length/width/height) and a diameter may coexist. Weight in
    // whole grams. All optional; format via `measure.ts`.
    dimensionUnit: DimensionUnit.default('INCH'),
    lengthCi: z.number().int().positive().nullable().optional(),
    widthCi: z.number().int().positive().nullable().optional(),
    heightCi: z.number().int().positive().nullable().optional(),
    diameterCi: z.number().int().positive().nullable().optional(),
    weightGrams: z.number().int().positive().nullable().optional(),
    isActive: z.boolean(),
    createdById: Id.nullable().optional(),
    updatedAt: z.iso.datetime()
});
export const Lead = z.object({
    id: Id,
    // Generated client-side before submit; the idempotency key the sync engine
    // replays against.
    clientUuid: z.uuidv4(),
    // Null until the server allocates it on sync; the UI shows "Pending".
    ref: z.number().int().nullable(),
    eventId: Id,
    company: z.string().min(1),
    contactName: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    email: z.email().nullable().optional(),
    country: z.string().nullable().optional(),
    // Display COVERS (~1600px). The pristine full-res originals live on the
    // *OriginalKey fields; the lightbox zooms/downloads those, the tiles show
    // these. Null-original (legacy card) falls back to the cover for zoom/edit.
    cardPhotoKey: z.string().nullable().optional(),
    cardBackKey: z.string().nullable().optional(),
    cardFrontOriginalKey: z.string().nullable().optional(),
    cardBackOriginalKey: z.string().nullable().optional(),
    stage: LeadStage,
    advance: MoneyAmount.nullable().optional(),
    paymentMode: PaymentMode.nullable().optional(),
    currency: CurrencyCode,
    followUpDate: z.iso.date().nullable().optional(),
    notes: z.string().nullable().optional(),
    isActive: z.boolean(),
    createdById: Id,
    // The author's display name, denormalised onto the wire so the board and
    // detail can name them offline — the mirror holds leads, never users, so a
    // `createdById` alone renders as an unresolvable cuid in a dead zone.
    // Optional/nullable so a client on a cached payload from before this field
    // existed still validates rather than failing the whole mirror refresh.
    createdByName: z.string().nullable().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime()
});
export const LeadItem = z.object({
    id: Id,
    leadId: Id,
    productId: Id,
    // Snapshotted at the moment of quoting so an old quote stays readable after
    // the catalogue changes.
    productNameSnapshot: z.string().min(1),
    quantity: z.number().int().min(1),
    listPriceSnapshot: MoneyAmount.nullable().optional(),
    quotedPrice: MoneyAmount,
    note: z.string().nullable().optional()
});
export const LeadEvent = z.object({
    id: Id,
    leadId: Id,
    stage: LeadStage,
    note: z.string().nullable().optional(),
    at: z.iso.datetime(),
    userId: Id
});
// ---------------------------------------------------------------------------
// Create/update input shapes
// ---------------------------------------------------------------------------
// A quoted line as it is captured, before the lead it belongs to exists. No
// `id` and no `leadId`: both are assigned server-side when the parent lead is
// inserted, and the device has neither at capture time.
export const CreateLeadItemInput = LeadItem.omit({ id: true, leadId: true });
// Items are nested, not a separate mutation. A lead captured at a booth carries
// its quoted lines, its card photo and its identity in ONE outbox entry, under
// ONE `clientUuid`, written in ONE transaction — see "The card photo travels
// inside the lead mutation" in docs/plan.md. Splitting items into their own
// mutation would force the offline drain to reach forward and rewrite a later
// queue entry with the returned lead id, which is the two-queues-to-reconcile
// design the plan explicitly rejects.
export const CreateLeadInput = z.object({
    clientUuid: z.uuidv4(),
    eventId: Id,
    items: z.array(CreateLeadItemInput).default([]),
    company: z.string().min(1),
    contactName: z.string().min(1).nullable().optional(),
    phone: z.string().min(1).nullable().optional(),
    email: z.email().nullable().optional(),
    country: z.string().min(1).nullable().optional(),
    // Passed through on edit to PRESERVE an unchanged card (cover + its original)
    // when no new file part accompanies this mutation. A new capture sends the
    // file instead and the server mints both keys; these carry the existing ones.
    cardPhotoKey: z.string().min(1).nullable().optional(),
    cardBackKey: z.string().min(1).nullable().optional(),
    cardFrontOriginalKey: z.string().min(1).nullable().optional(),
    cardBackOriginalKey: z.string().min(1).nullable().optional(),
    stage: LeadStage.optional(),
    // The optional note that becomes the FIRST `LeadEvent` for this lead (the
    // capture form's "How Keen Are They?" note). Distinct from `notes` (the
    // lead's free-text notes field). After creation, further stage notes go
    // through the dedicated stage-change endpoint (`StageChangeInput`).
    stageNote: z.string().nullable().optional(),
    advance: MoneyAmount.nullable().optional(),
    paymentMode: PaymentMode.nullable().optional(),
    currency: CurrencyCode.optional(),
    followUpDate: z.iso.date().nullable().optional(),
    notes: z.string().nullable().optional()
});
// eventId and clientUuid identify which lead and are never revised in place;
// everything else on a lead may be edited after capture.
//
// `items` is inherited from CreateLeadInput and is therefore optional here.
// When present it REPLACES the whole set rather than patching it — the edit
// form owns the entire item list and submits it whole, so there is no add-one
// or remove-one operation to express. Absent means "leave the items alone".
//
// `stageNote` is deliberately OMITTED (not merely inherited-and-unused) —
// unlike CreateLeadInput's stageNote, which becomes the note on a lead's
// FIRST LeadEvent, a stage move on an EXISTING lead only ever goes through
// the dedicated `POST /api/leads/:id/stage` (`StageChangeInput`), which is
// where a note for that move actually belongs. Carrying `stageNote` here too
// would let a caller send `stage` + `stageNote` in the same PATCH and have
// the note silently dropped — the API accepting a field it cannot honour.
// The edit screen already reflects this: it omits `stage` from its PATCH
// body entirely and calls `/stage` separately when the stage changed (see
// apps/web's `$leadId.edit.tsx`).
export const UpdateLeadInput = CreateLeadInput.omit({ clientUuid: true, eventId: true, stageNote: true }).partial();
export const CreateProductInput = z.object({
    // Business-assigned, not server-generated (decision changed 2026-07-24): the
    // office enters the customer-facing item number and it must be unique. The
    // client checks uniqueness against the catalogue mirror as you type; the DB's
    // `Product.itemNo @unique` is the authoritative backstop (a collision surfaces
    // as CONFLICT). The server-side allocator is kept but no longer called for
    // products. See .claude/rules/agent-memory.md.
    itemNo: z.number().int().positive(),
    name: z.string().min(1),
    categoryId: Id.nullable().optional(),
    // The full set of material ids for the product. On update, a present array
    // REPLACES the product's material tags; omitted leaves them unchanged.
    materialIds: z.array(Id).optional(),
    // The full set of finish ids. On update, a present array REPLACES the
    // product's finish tags; omitted leaves them unchanged. Same contract as
    // `materialIds` above.
    finishIds: z.array(Id).optional(),
    listPrice: MoneyAmount.nullable().optional(),
    currency: CurrencyCode.optional(),
    // Optional size (hundredths of `dimensionUnit`) + weight (grams). Box
    // dimensions and a diameter may coexist. See `measure.ts`.
    dimensionUnit: DimensionUnit.default('INCH'),
    lengthCi: z.number().int().positive().nullable().optional(),
    widthCi: z.number().int().positive().nullable().optional(),
    heightCi: z.number().int().positive().nullable().optional(),
    diameterCi: z.number().int().positive().nullable().optional(),
    weightGrams: z.number().int().positive().nullable().optional()
});
// itemNo is editable on update (correcting a typo) — `.partial()` makes it
// optional here, and the route enforces uniqueness the same way as create.
export const UpdateProductInput = CreateProductInput.partial();
/**
 * The bulk event-tagging input (`EventProduct`). Ids are capped at 500 —
 * comfortably above the whole ~152-item catalogue — because a public endpoint
 * needs a bound to stop it accepting an arbitrarily long array. `tagged: true`
 * is "Select all shown"; `tagged: false` is "Clear shown".
 */
export const BulkEventTagInput = z.object({
    eventId: Id,
    productIds: z.array(Id).min(1).max(500),
    tagged: z.boolean()
});
//# sourceMappingURL=entities.js.map